import { useState, useCallback, useEffect, useRef } from "react";
import {
  generatePresignedUrl,
  uploadFileToS3,
} from "../../../../services/contentService";
import { CognitoAuthService } from "../../../../services/cognitoService";
import { sanitizeFileName } from "../../../../utils/fileUtils";
import {
  updateAgentTrainingFiles,
  fetchAgentDetails,
} from "../../../../services/agentService";
import { useJobStatusPolling } from "./useJobStatusPolling";
import { normalizeFiles, NormalizedFile } from "../../utils/fileNormalization";
import { debug } from "../../../../utils/debugLogger";

interface UseAgentKnowledgeFilesOptions {
  selectedCustomer: { customer_id: string } | null;
  selectedAgentId: string;
  agentDetails: any;
  idToken: string;
  onMessageChange: (message: string) => void;
}

/**
 * How often to re-check whether files still being indexed have finished, and how long
 * to keep checking.
 *
 * The ceiling matches KNOWLEDGE_FILE_PROCESSING_GRACE_SECONDS in create_agent/app.py:
 * past that point the backend stops reporting "processing" and starts reporting
 * "failed", so there is nothing further to wait for and polling would only burn
 * requests against a verdict that will not change.
 */
const INDEXING_POLL_INTERVAL_MS = 10_000;
const INDEXING_POLL_TIMEOUT_MS = 5 * 60 * 1000;

export const useAgentKnowledgeFiles = (options: UseAgentKnowledgeFilesOptions) => {
  const {
    selectedCustomer,
    selectedAgentId,
    agentDetails,
    idToken,
    onMessageChange,
  } = options;

  const [knowledgeFiles, setKnowledgeFiles] = useState<NormalizedFile[]>([]);
  const [originalKnowledgeFiles, setOriginalKnowledgeFiles] = useState<NormalizedFile[]>([]);
  const [newKnowledgeFiles, setNewKnowledgeFiles] = useState<File[]>([]);
  const [savingKnowledgeFiles, setSavingKnowledgeFiles] = useState<boolean>(false);

  const { startPolling, stopPolling } = useJobStatusPolling();

  // --- Keep "Processing" badges honest ------------------------------------------
  //
  // The job polling above ends as soon as the files are ATTACHED to the agent, but
  // indexing runs separately: the embedding pipeline is triggered by an S3 event and
  // finishes seconds-to-minutes later. Without this, a file's badge stays "Processing"
  // until the user happens to reload — on a feature whose whole purpose is showing
  // status, a badge frozen on "Processing" reads as broken.
  //
  // Only the file list is replaced on each tick. Anything the user is mid-way through
  // (newly picked files, pending removals) is deliberately left untouched, so a poll
  // landing while they are editing cannot discard their staged changes.
  const pollDeadlineRef = useRef<number>(0);
  const hasFilesIndexing = knowledgeFiles.some((f) => f.status === "processing");

  useEffect(() => {
    // Don't poll over an in-flight save — that path owns the list and will set it
    // itself when the job finishes.
    if (!hasFilesIndexing || savingKnowledgeFiles) return;
    if (!selectedCustomer?.customer_id || !selectedAgentId || !idToken) return;

    // Fresh window each time indexing starts; a later upload gets its own full budget.
    pollDeadlineRef.current = Date.now() + INDEXING_POLL_TIMEOUT_MS;
    let cancelled = false;

    const timer = setInterval(async () => {
      if (Date.now() > pollDeadlineRef.current) {
        clearInterval(timer);
        return;
      }
      try {
        const details = await fetchAgentDetails(
          selectedAgentId,
          selectedCustomer.customer_id,
          idToken
        );
        if (cancelled) return;
        const refreshed = normalizeFiles(
          Array.isArray(details?.training_files) ? details.training_files : []
        );
        setKnowledgeFiles((prev) => {
          // Re-check on the latest state rather than the value captured when this
          // interval was created: the user may have removed a row since then, and
          // overwriting would silently resurrect it.
          const stillPending = prev.some((f) => f.status === "processing");
          return stillPending ? refreshed : prev;
        });
        setOriginalKnowledgeFiles((prev) =>
          prev.some((f) => f.status === "processing") ? refreshed : prev
        );
      } catch (error) {
        // A transient failure must not kill the watch — the next tick retries, and
        // the deadline still bounds it.
        debug.error("Knowledge file status poll failed:", error);
      }
    }, INDEXING_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    hasFilesIndexing,
    savingKnowledgeFiles,
    selectedAgentId,
    selectedCustomer?.customer_id,
    idToken,
  ]);

  const computeRemovedKnowledgeFiles = (): NormalizedFile[] => {
    const currentKeys = new Set(knowledgeFiles.map((f) => f.key));
    return originalKnowledgeFiles.filter((f) => !currentKeys.has(f.key));
  };

  const handleSaveKnowledgeFiles = async () => {
    if (
      !selectedCustomer ||
      !selectedCustomer.customer_id ||
      !selectedAgentId ||
      !agentDetails ||
      !idToken
    ) {
      onMessageChange("Please select a customer and agent first.");
      return;
    }

    const removed = computeRemovedKnowledgeFiles();

    if (newKnowledgeFiles.length === 0 && removed.length === 0) {
      onMessageChange("No knowledge file changes to save.");
      return;
    }

    // Clear any existing polling
    stopPolling();

    setSavingKnowledgeFiles(true);
    onMessageChange("Saving knowledge files...");

    // Track whether S3 uploads succeeded before backend update
    let didUploadAny = false;
    const uploadedKnowledgeFiles: NormalizedFile[] = [];

    try {
      // 1) Upload newly added knowledge files (training data)
      if (newKnowledgeFiles.length > 0) {
        onMessageChange("Uploading knowledge files...");
        const user_email = CognitoAuthService.getUserEmail(idToken);

        for (const file of newKnowledgeFiles) {
          const sanitizedName = sanitizeFileName(file.name);
          const uploadFile = sanitizedName === file.name ? file : new File([file], sanitizedName, { type: file.type });

          const presignedResponse = await generatePresignedUrl(
            {
              customer_id: selectedCustomer.customer_id,
              user_email,
              is_training_data: true,
              training_data_content_type: uploadFile.type,
              file_name: uploadFile.name,
              image_content_type: "",
              video_content_type: "",
              image_type: "",
              video_type: "",
              logo: false,
            },
            idToken
          );

          if (
            presignedResponse.trainingData &&
            presignedResponse.trainingData.uploadURL &&
            presignedResponse.trainingData.fileInfo
          ) {
            await uploadFileToS3(
              presignedResponse.trainingData.uploadURL,
              uploadFile,
              presignedResponse.trainingData.fileInfo.contentType
            );
            uploadedKnowledgeFiles.push({
              bucket: presignedResponse.trainingData.fileInfo.bucket,
              key: presignedResponse.trainingData.fileInfo.key,
              filename: presignedResponse.trainingData.fileInfo.filename,
            });
            didUploadAny = true;
          } else {
            throw new Error(
              "Failed to get presigned URL for knowledge file upload"
            );
          }
        }
      }

      // 2) Update add/remove via backend (PUT action=update_files)
      onMessageChange("Updating knowledge file attachments...");
      const jobResponse = await updateAgentTrainingFiles(
        {
          customer_id: selectedCustomer.customer_id,
          aiagent_id: selectedAgentId,
          add_files:
            uploadedKnowledgeFiles.length > 0
              ? uploadedKnowledgeFiles
              : undefined,
          remove_files:
            removed.length > 0
              ? removed.map((f) => ({ bucket: f.bucket, key: f.key }))
              : undefined,
        },
        idToken
      );

      // 3) If we got a job_id, start polling for job status
      if (jobResponse.job_id) {
        onMessageChange("Processing knowledge files... This may take a moment.");

        startPolling({
          jobId: jobResponse.job_id,
          customerId: selectedCustomer.customer_id,
          agentId: selectedAgentId,
          idToken,
          fetchAgentDetails,
          onSuccess: (details: any) => {
            // Refresh knowledge files from updated agent details
            const rawFiles = Array.isArray(details.training_files)
              ? details.training_files
              : [];
            const normalizedFiles = normalizeFiles(rawFiles);

            setKnowledgeFiles(normalizedFiles);
            setOriginalKnowledgeFiles(normalizedFiles);
            setNewKnowledgeFiles([]);
            setSavingKnowledgeFiles(false);
            onMessageChange("✅ Knowledge files updated successfully!");
          },
          onFailure: (errorMessage: string) => {
            setSavingKnowledgeFiles(false);
            onMessageChange(
              `❌ ${errorMessage}${
                didUploadAny ? " (Files were uploaded to S3)" : ""
              }`
            );
          },
          onStatusUpdate: (status: string) => {
            onMessageChange(
              `Processing knowledge files... (Status: ${status || "PENDING"})`
            );
          },
          pollInterval: 10000, // 10 seconds
          timeout: 5 * 60 * 1000, // 5 minutes
        });
      } else {
        // No job_id (backward compatibility or immediate success)
        // Update local state directly
        const merged = [...knowledgeFiles, ...uploadedKnowledgeFiles];
        const byKey = new Map<string, NormalizedFile>();
        merged.forEach((f) => {
          if (f && f.key) byKey.set(f.key, f);
        });
        const finalFiles = Array.from(byKey.values());

        setKnowledgeFiles(finalFiles);
        setOriginalKnowledgeFiles(finalFiles);
        setNewKnowledgeFiles([]);
        setSavingKnowledgeFiles(false);
        onMessageChange("✅ Knowledge files updated successfully!");
      }
    } catch (error) {
      // Clear polling on error
      stopPolling();

      debug.error("Error saving knowledge files:", error);
      const errMsg =
        error instanceof Error ? error.message : "Unknown error occurred";

      if (errMsg.toLowerCase().includes("presigned")) {
        onMessageChange(
          "Failed to upload one or more knowledge files to S3 (could not get upload URL). Please try again."
        );
      } else if (errMsg.toLowerCase().includes("upload")) {
        onMessageChange(
          "Failed to upload one or more knowledge files to S3. Please try again."
        );
      } else if (errMsg.toLowerCase().includes("update agent training files")) {
        onMessageChange(
          "Files were uploaded, but updating the agent record failed. Please try saving knowledge files again."
        );
      } else {
        onMessageChange(
          didUploadAny
            ? "Files were uploaded, but updating the agent record failed. Please try saving knowledge files again."
            : "Failed to update the agent record with file changes. Please try again."
        );
      }
      setSavingKnowledgeFiles(false);
    }
  };

  const removeKnowledgeFile = (key: string) => {
    setKnowledgeFiles((prev) => prev.filter((f) => f.key !== key));
  };

  const addKnowledgeFiles = (files: File[]) => {
    setNewKnowledgeFiles((prev) => [...prev, ...files]);
  };

  const removeNewFile = (index: number) => {
    setNewKnowledgeFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const initializeKnowledgeFiles = useCallback((details: any) => {
    const rawFiles = Array.isArray(details.training_files)
      ? details.training_files
      : [];
    const normalizedFiles = normalizeFiles(rawFiles);
    setKnowledgeFiles(normalizedFiles);
    setOriginalKnowledgeFiles(normalizedFiles);
    setNewKnowledgeFiles([]);
  }, []);

  const resetKnowledgeFiles = useCallback(() => {
    setKnowledgeFiles([]);
    setOriginalKnowledgeFiles([]);
    setNewKnowledgeFiles([]);
  }, []);

  return {
    knowledgeFiles,
    newKnowledgeFiles,
    savingKnowledgeFiles,
    handleSaveKnowledgeFiles,
    removeKnowledgeFile,
    addKnowledgeFiles,
    removeNewFile,
    initializeKnowledgeFiles,
    resetKnowledgeFiles,
  };
};
